import { leaveForPayment } from '../../../helpers/shop/couponSession';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PaymentMethods from './PaymentMethods';
import usePaymentStatusPolling from './usePaymentStatusPolling';

import { fillOrderData, checkoutPaymentResolved } from '../../../actions/shop';
import { basicMsg, simpleConfirmDialog } from '../../../actions/swal_msg';

import { paypalPayment } from '../../../actions/payment/paypal_payment';
import { openPayPayment } from '../../../actions/payment/openpay_payment';
import { mercadoPagoPayment } from '../../../actions/payment/mercadopago_payment';

import {
    Container, Grid, Card, CardContent, CardActions, Typography,
    Button, Avatar, LinearProgress, Box, Skeleton, Stack, useMediaQuery, Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';

//images
import logopaypal from '../../../assets/images/paypal.png'
import logoopenpay from '../../../assets/images/openpay-color.png'
import logomercadopago from '../../../assets/images/mercado-pago.png'

const PaypalButton = styled(Button)(({ theme }) => ({
    color: '#000',
    backgroundColor: '#FFC439',
    fontWeight: '600',
    textTransform: 'none',
    height: '50px',
    '&:hover': {
        backgroundColor: '#e0ae2f',
    },
}));

const OpenpayButton = styled(Button)(({ theme }) => ({
    color: '#fff',
    backgroundColor: '#7C4DFF',
    fontWeight: '600',
    textTransform: 'none',
    height: '50px',
    '&:hover': {
        backgroundColor: '#4a23c1',
    },
}));

const MercadoPButton = styled(Button)(({ theme }) => ({
    color: '#000',
    backgroundColor: '#FFE600',
    fontWeight: '600',
    textTransform: 'none',
    height: '50px',
    '&:hover': {
        backgroundColor: '#0082bb',
        color: '#fff'
    },
}));

const FastCheckout = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const matches = useMediaQuery('(max-width: 950px)');

    // bandera [carga de productos]
    const { loading: { shop_products } } = useSelector(state => state.ui);
    const {
        cart: { checkout: { skip, order } }
    } = useSelector(state => state.shop);

    // régimen fiscal y CFDI disponible
    const { taxSystemData, CFDIData } = useSelector(state => state.checkout);

    const { _id, order_num, summary, coupon, address, billing, payment, shipping, products } = order;
    const { discount, promos_discount, subtotal, total } = summary;
    const {
        name, lastname, email, tel, street, ext_num, int_num,
        postalcode, colony, city, state,
        crossing_streets, references, residence_type
    } = address;
    const {
        req_invoice,
        data: {
            business_name, rfc, cif, tax_system, cfdi,
            b_tel, b_address, b_postalcode, b_colony,
            b_city, b_state
        }
    } = billing;
    const { method, text } = payment;
    const { partner, cost } = shipping;

    const [activeMethod, setActiveMethod] = useState('');
    // botón
    const [disabled, setDisabled] = useState(false);

    const showPaidOrder = useCallback((reference) => {
        const orderReference = reference || payment.nefro_reference;
        if (!orderReference) return;
        dispatch(checkoutPaymentResolved());
        navigate(`/gracias-por-tu-compra/${orderReference}`, {
            replace: true,
            state: { paymentAlreadyConfirmed: true }
        });
    }, [dispatch, navigate, payment.nefro_reference]);

    useEffect(() => {
        if (!shop_products) { // terminó de cargar tienda y carrito
            if (!skip) // redireccionar a carrito si no se encontró pedido pendiente
                navigate('/carrito');
        }
    }, [shop_products]);

    useEffect(() => {
        setActiveMethod(method);
    }, [method]);

    usePaymentStatusPolling({ orderId: _id, enabled: skip, onPaid: showPaidOrder });

    const goToCheckout = async (msg) => {
        if (await simpleConfirmDialog(
            `¿Desea editar su información de ${msg}? Será redireccionado al checkout`, 'Sí, continuar'
        )) {
            sessionStorage.setItem('shipping-data', JSON.stringify(address));
            const {
                req_invoice, data: {
                    b_tel, b_address, b_postalcode,
                    b_colony, b_city, b_state, ...my_billing
                }
            } = billing;
            sessionStorage.setItem('billing-data', JSON.stringify({
                req_invoice,
                ...my_billing,
                billing_tel: b_tel,
                address: b_address,
                billing_postalcode: b_postalcode,
                billing_colony: b_colony,
                billing_city: b_city,
                billing_state: b_state
            }));
            dispatch(fillOrderData(true));
            navigate('/checkout'); // { state: { fill: true } }
        }
    };

    const payNow = async () => {
        if (!['MercadoPago', 'PayPal', 'OpenPay'].includes(activeMethod)) {
            basicMsg('Selecciona un método de pago');
            return;
        }

        setDisabled(true);
        let result;
        if (activeMethod === 'MercadoPago')
            result = await dispatch(mercadoPagoPayment(_id, setDisabled));
        else if (activeMethod === 'PayPal')
            result = await dispatch(paypalPayment(_id, setDisabled));
        else
            result = await dispatch(openPayPayment(_id, setDisabled));

        if (result?.status && result.resp?.payment_url) {
            leaveForPayment(result.resp.payment_url, {
                orderId: _id,
                context: 'fast-checkout'
            });
            return;
        }
        if (result?.msg === 'order-already-paid') {
            showPaidOrder(result.resp?.reference);
            return;
        }
        if (result?.msg === 'payment-method-active') {
            const provider = result.resp?.provider;
            const continuePayment = await simpleConfirmDialog(
                `Ya tienes un pago pendiente en ${provider}. Para evitar un doble cobro debes continuar con ese método.`,
                `Continuar con ${provider}`
            );
            setActiveMethod(provider || '');
            if (continuePayment && result.resp?.payment_url)
                leaveForPayment(result.resp.payment_url, {
                    orderId: _id,
                    context: 'fast-checkout'
                });
        }
    };

    return (
        <Container maxWidth="xxl">
            {
                shop_products ?
                    <>
                        <Box sx={{ width: '100%', mt: matches ? 6 : 18 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Cargando tu pedido, por favor espera...
                            </Typography>
                            <LinearProgress />
                        </Box>
                        {
                            !matches ?
                                <Grid container spacing={2}>
                                    <Grid item xl={8} lg={8} md={6} sm={12} xs={12}>
                                        <Skeleton height={500} />
                                    </Grid>
                                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                        <Skeleton height={500} />
                                    </Grid>
                                </Grid>
                                :
                                <Skeleton height={500} />
                        }
                    </>
                    :
                    <Box sx={{ my: matches ? 5 : 18 }}>
                        <Stack direction="column" spacing={.5}>
                            <Typography variant="body" sx={{ fontSize: '28px', fontWeight: '600' }}>
                                {`Pedido #${order_num}`}
                            </Typography>
                            <Box>
                                <Typography variant="h6">{`Método de pago activo: ${method}`}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {text}
                                </Typography>
                            </Box>
                        </Stack>
                        <Grid container spacing={4} sx={{ mt: 1 }}>
                            <Grid item xxl={6} xl={6} lg={6} md={12} sm={12} xs={12} order={{ xxl: 1, xl: 1, lg: 1, md: 2, sm: 2, xs: 2 }}>

                                <Typography variant="h5" component="div"
                                    sx={{ mb: 2 }}>
                                    Información de pago
                                </Typography>
                                <PaymentMethods
                                    activeMethod={activeMethod}
                                    setActiveMethod={setActiveMethod}
                                />

                                <Box sx={{ mt: 2 }}>
                                    {
                                        activeMethod === 'PayPal'
                                            ?
                                            <PaypalButton
                                                variant="contained"
                                                onClick={payNow}
                                                endIcon={
                                                    <img src={logopaypal} alt={logopaypal} style={{ width: '100%', height: 24, objectFit: 'cover' }} />
                                                }
                                                disabled={disabled}
                                            >
                                                Pagar ahora
                                            </PaypalButton>
                                            :
                                            activeMethod === 'OpenPay'
                                                ?
                                                <OpenpayButton
                                                    variant="contained"
                                                    onClick={payNow}
                                                    endIcon={
                                                        <img src={logoopenpay} alt={logoopenpay} style={{ width: '100%', height: 24, objectFit: 'cover' }} />
                                                    }
                                                    disabled={disabled}
                                                >
                                                    Pagar ahora
                                                </OpenpayButton>
                                                : activeMethod === 'MercadoPago'
                                                ?
                                                <MercadoPButton
                                                    variant="contained"
                                                    onClick={payNow}
                                                    endIcon={
                                                        <img src={logomercadopago} alt={logomercadopago} style={{ width: '100%', height: 24, objectFit: 'cover' }} />
                                                    }
                                                    disabled={disabled}
                                                >
                                                    Pagar ahora
                                                </MercadoPButton>
                                                :
                                                <Button variant="contained" disabled>
                                                    Selecciona un método de pago
                                                </Button>
                                    }
                                </Box>

                                <Card sx={{ width: '100%', mt: 3, boxShadow: '0 2px 4px 2px rgb(0 0 0 / 20%)' }}>
                                    <CardContent>
                                        <Typography variant="h5" component="div"
                                            sx={{ mb: 2 }}>
                                            Dirección de envío
                                        </Typography>
                                        <Typography variant="body1">
                                            {`${name} ${lastname}`}
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {`Correo electrónico: ${email}, Tel: ${tel}`}
                                        </Typography>
                                        <Typography variant="body2">
                                            {`${street} #${ext_num} ${int_num ? `interior ${int_num} ` : ''}CP. ${postalcode}`}
                                        </Typography>
                                        <Typography variant="body2">
                                            {`Colonia ${colony}. ${city}, ${state}`}
                                        </Typography>
                                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                            Referencias
                                        </Typography>
                                        <Typography variant="body2">
                                            {references}
                                        </Typography>
                                        <Typography variant="body2">
                                            {`Entre calles: ${crossing_streets}`}
                                        </Typography>
                                        <Typography variant="body2">
                                            {`Domicilio: ${residence_type}`}
                                        </Typography>
                                    </CardContent>
                                    <CardActions>
                                        <Button size="small"
                                            onClick={() => goToCheckout('envío')}>
                                            Editar
                                        </Button>
                                    </CardActions>
                                </Card>
                                <Card sx={{ width: '100%', mt: 3, boxShadow: '0 2px 4px 2px rgb(0 0 0 / 20%)' }}>
                                    <CardContent>
                                        <Typography variant="h5" component="div"
                                            sx={{ mb: 2 }}>
                                            Dirección de facturación
                                        </Typography>
                                        {
                                            req_invoice ?
                                                <>
                                                    <Typography variant="body1">
                                                        {`Razón social: ${business_name}`}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {`RFC: ${rfc}`}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <b>Régimen fiscal: </b>
                                                        {tax_system} - {
                                                            taxSystemData.map(({ codigo, descripcion }) => {
                                                                if (codigo === tax_system) return descripcion;
                                                                else return '';
                                                            })
                                                        }
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <b>CFDI: </b>
                                                        {cfdi} - {
                                                            CFDIData.map(({ codigo, descripcion }) => {
                                                                if (codigo === cfdi) return descripcion;
                                                                else return '';
                                                            })
                                                        }
                                                    </Typography>
                                                    <Typography variant='body2'>
                                                        {`Número telefónico: ${b_tel}`}
                                                    </Typography>
                                                    <Typography variant='body2'>{`${b_address}, CP: ${b_postalcode}`}</Typography>
                                                    <Typography variant='body2'>
                                                        {`Colonia ${b_colony}. ${b_city}, ${b_state}`}
                                                    </Typography>
                                                    <Typography variant="body1" component="div">
                                                        <b style={{ fontWeight: '600' }}>{`Documento CIF: `}</b>
                                                        <a target="_blank" rel="noreferrer"
                                                            href={`https://drive.google.com/file/d/${cif}/view`}>
                                                            abrir
                                                        </a>
                                                    </Typography>
                                                </>
                                                :
                                                <Typography variant='body1'>
                                                    No requiere factura
                                                </Typography>
                                        }
                                    </CardContent>
                                    <CardActions>
                                        <Button size="small"
                                            onClick={() => goToCheckout('facturación')}>
                                            Editar
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>

                            <Grid item xxl={6} xl={6} lg={6} md={12} sm={12} xs={12} order={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                                <Card sx={{ width: '100%', boxShadow: '0 2px 4px 2px rgb(0 0 0 / 20%)' }}>
                                    <CardContent>
                                        <Typography variant="h5" component="div"
                                            sx={{ mb: 5 }}>
                                            Productos:
                                        </Typography>
                                        {
                                            products.length > 0 &&
                                            <>
                                                {
                                                    products.map(({ title, price_text, qty_text, img }, i) =>
                                                        <Box key={title} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 5 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1.5, alignItems: 'center' }}>
                                                                <Box sx={{ position: 'relative' }}>
                                                                    <Box
                                                                        component='img'
                                                                        src={img.id}
                                                                        alt={img.alt}
                                                                        sx={{ width: '100px', objectFit: 'cover' }}
                                                                    />
                                                                    <Box sx={{ position: 'absolute', top: -10, right: -10 }}>
                                                                        <Avatar
                                                                            sx={{ fontFamily: 'sans-serif', width: '25px', height: '25px', fontSize: '12px', bgcolor: '#38B6FF' }}
                                                                        >
                                                                            {qty_text?.[1]}
                                                                        </Avatar>
                                                                    </Box>
                                                                </Box>
                                                                {/* <img src={img.id} alt={img.at} width='100' style={{ objectFit: 'cover' }} /> */}
                                                                <Typography variant='body1'>
                                                                    {title}
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant='body2' color='text.secondary'>
                                                                {price_text}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                }
                                            </>
                                        }
                                        <Typography variant="h5" component="div"
                                            sx={{ mb: 2 }}>
                                            Resumen de carrito
                                        </Typography>
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                                                <Typography variant="body1">
                                                    Subtotal:
                                                </Typography>
                                                <Typography variant="body1">
                                                    ${subtotal}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                                                <Typography variant="body1">
                                                    Método de envío:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {partner}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                                                <Typography variant="body1">
                                                    Costo de envío:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {`${cost === 0 ? 'Gratis' : `$${cost}`}`}
                                                </Typography>
                                            </Box>
                                            {
                                                discount !== 0 &&
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        <Box>
                                                            <Typography variant="body1">
                                                                Descuento por cupón:
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {coupon.text}
                                                            </Typography>
                                                        </Box>
                                                        <Chip label={coupon.name} size='small' color='primary' />
                                                    </Box>
                                                    <Typography variant="body1" color='error'>
                                                        {discount}
                                                    </Typography>
                                                </Box>
                                            }
                                            {
                                                promos_discount !== 0 &&
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                                                    <Typography variant="body1">
                                                        Descuentos promociones:
                                                    </Typography>
                                                    <Typography variant="body1" color='error'>
                                                        - ${promos_discount}
                                                    </Typography>
                                                </Box>
                                            }
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                                                <Typography variant="body1">
                                                    Total:
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                                                    ${total}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                    {
                                        !matches ?
                                            <CardActions>
                                                {
                                                    activeMethod === 'PayPal'
                                                        ?
                                                        <PaypalButton
                                                            variant="contained"
                                                            onClick={payNow}
                                                            endIcon={
                                                                <img src={logopaypal} alt={logopaypal} style={{ width: '100%', height: 24, objectFit: 'cover' }} />
                                                            }
                                                            disabled={disabled}
                                                        >
                                                            Pagar ahora
                                                        </PaypalButton>
                                                        :
                                                        activeMethod === 'OpenPay'
                                                            ?
                                                            <OpenpayButton
                                                                variant="contained"
                                                                onClick={payNow}
                                                                endIcon={
                                                                    <img src={logoopenpay} alt={logoopenpay} style={{ width: '100%', height: 24, objectFit: 'cover' }} />
                                                                }
                                                                disabled={disabled}
                                                            >
                                                                Pagar ahora
                                                            </OpenpayButton>
                                                            : activeMethod === 'MercadoPago'
                                                            ?
                                                            <MercadoPButton
                                                                variant="contained"
                                                                onClick={payNow}
                                                                endIcon={
                                                                    <img src={logomercadopago} alt={logomercadopago} style={{ width: '100%', height: 24, objectFit: 'cover' }} />
                                                                }
                                                                disabled={disabled}
                                                            >
                                                                Pagar ahora
                                                            </MercadoPButton>
                                                            :
                                                            <Button variant="contained" disabled>
                                                                Selecciona un método de pago
                                                            </Button>
                                                }
                                            </CardActions>
                                            :
                                            null
                                    }
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
            }
        </Container>
    );
};

export default FastCheckout;
